import React, { useEffect, useState } from "react";
import { Form, Input, Button, Card, Spin, Tabs, Space, Popconfirm } from "antd";
import { Save, Sparkles, Plus, Trash2 } from "lucide-react";
import {
  useGetCmsConfig,
  useSaveCmsConfig,
} from "../../../hooks/api/cms/hooks";
import {
  ISculptureCareConfigData,
  ISculptureCareTab,
} from "../../../types/api/sculpture-care";
import TinyMce from "../../../utils/TinyMCE";
import { useMessageApi } from "../../../provider/AntdNotification";

const defaultTabs: ISculptureCareTab[] = [
  { id: "1", title: "Bronze", content: "" },
  { id: "2", title: "Granite", content: "" },
  { id: "3", title: "Marble", content: "" },
  { id: "4", title: "Resin", content: "" },
  { id: "5", title: "Stainless Steel", content: "" },
];

const SculptureCareCMS: React.FC = () => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();
  const { data: configData, isLoading } =
    useGetCmsConfig<ISculptureCareConfigData>("SCLUPTURE_CARE");
  const saveMutation = useSaveCmsConfig<ISculptureCareConfigData>({
    page: "SCLUPTURE_CARE",
    onSuccess: () => {
      messageApi.success("Sculpture Care configuration saved successfully.");
    },
    onError: (err: any) => {
      messageApi.error(err?.message ?? "Failed to save configuration");
    },
  });

  const [tabs, setTabs] = useState<ISculptureCareTab[]>(defaultTabs);
  const [activeTabKey, setActiveTabKey] = useState<string>("1");

  useEffect(() => {
    if (configData?.data) {
      const data: ISculptureCareConfigData = configData.data;
      if (data.tabs && Array.isArray(data.tabs) && data.tabs.length > 0) {
        setTabs(data.tabs);
        setActiveTabKey(data.tabs[0].id);
      } else {
        // Fallback for legacy format
        const legacyTabs: ISculptureCareTab[] = [
          { id: "1", title: "Bronze", content: (data as any).bronze || "" },
          { id: "2", title: "Granite", content: (data as any).granite || "" },
          { id: "3", title: "Marble", content: (data as any).marble || "" },
          { id: "4", title: "Resin", content: (data as any).resin || "" },
          {
            id: "5",
            title: "Stainless Steel",
            content: (data as any).stainlessSteel || "",
          },
        ];
        setTabs(legacyTabs);
        setActiveTabKey("1");
      }
      form.setFieldsValue({
        title: data.title || "",
        subtitle: data.subtitle || "",
        disclaimer: data.disclaimer || (data as any).disclaimer || "",
      });
    }
  }, [configData, form]);

  const addTab = () => {
    const newId = String(Date.now());
    const newTab: ISculptureCareTab = {
      id: newId,
      title: `Material ${tabs.length + 1}`,
      content: "",
    };
    const updated = [...tabs, newTab];
    setTabs(updated);
    setActiveTabKey(newId);
  };

  const removeTab = (targetId: string) => {
    if (tabs.length <= 1) return;
    const updated = tabs.filter((t) => t.id !== targetId);
    setTabs(updated);
    if (activeTabKey === targetId) {
      setActiveTabKey(updated[0]?.id || "1");
    }
  };

  const updateTabTitle = (id: string, newTitle: string) => {
    setTabs((prev) =>
      prev.map((tab) => (tab.id === id ? { ...tab, title: newTitle } : tab)),
    );
  };

  const updateTabContent = (id: string, newContent: string) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === id ? { ...tab, content: newContent } : tab,
      ),
    );
  };

  const onFinish = (values: any) => {
    const payload: ISculptureCareConfigData = {
      title: values.title || "",
      subtitle: values.subtitle || "",
      tabs,
      disclaimer: values.disclaimer || "",
    };
    saveMutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-12">
      <div className="w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
              <Sparkles className="text-primary" size={24} /> Sculpture Care CMS
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage dynamic care tabs and content using TinyMCE editor.
            </p>
          </div>
          <Button
            type="dashed"
            icon={<Plus size={16} />}
            onClick={addTab}
            className="flex items-center gap-2"
          >
            Add New Care Tab
          </Button>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="w-full"
        >
          {/* Page Header Section */}
          <Card
            title={
              <span className="font-semibold text-slate-800">Page Header</span>
            }
            className="w-full mb-6 shadow-sm"
          >
            <Form.Item name="title" label="Page Heading">
              <Input placeholder="Enter page heading..." />
            </Form.Item>
            <Form.Item name="subtitle" label="Page Description">
              <Input.TextArea
                rows={4}
                placeholder="Enter page description..."
              />
            </Form.Item>
          </Card>

          {/* Dynamic Tabs for Sculpture Care */}
          <Card className="w-full mb-6 shadow-sm">
            <Tabs
              activeKey={activeTabKey}
              onChange={setActiveTabKey}
              type="editable-card"
              onEdit={(targetKey, action) => {
                if (action === "add") addTab();
                if (action === "remove" && typeof targetKey === "string")
                  removeTab(targetKey);
              }}
              items={tabs.map((tab) => ({
                key: tab.id,
                label: (
                  <span className="font-medium text-slate-700">
                    {tab.title}
                  </span>
                ),
                children: (
                  <div className="pt-4 flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">
                          Tab Title / Material Name
                        </label>
                        <Input
                          value={tab.title}
                          onChange={(e) =>
                            updateTabTitle(tab.id, e.target.value)
                          }
                          placeholder="e.g. Bronze, Granite, Marble..."
                          className="max-w-md"
                        />
                      </div>
                      {tabs.length > 1 && (
                        <Popconfirm
                          title="Delete this tab?"
                          onConfirm={() => removeTab(tab.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            danger
                            type="text"
                            icon={<Trash2 size={16} />}
                            className="mt-5 flex items-center gap-1"
                          >
                            Remove Tab
                          </Button>
                        </Popconfirm>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-slate-500 mb-1 block">
                        Care Instructions & Content (TinyMCE)
                      </label>
                      <TinyMce
                        value={tab.content}
                        onChange={(val) => updateTabContent(tab.id, val)}
                      />
                    </div>
                  </div>
                ),
              }))}
            />
          </Card>

          {/* Disclaimer Card */}
          <Card
            title={
              <span className="font-semibold text-slate-800">Disclaimer</span>
            }
            className="w-full mb-6 shadow-sm"
          >
            <Form.Item name="disclaimer" className="mb-0">
              <Input.TextArea
                rows={4}
                placeholder="Enter the general care disclaimer text..."
              />
            </Form.Item>
          </Card>
        </Form>
      </div>

      {/* Always Visible Sticky Footer Save Bar */}
      <div className="sticky bottom-4 bg-white/95 backdrop-blur border border-slate-200 py-3 px-6 rounded-xl z-40 flex items-center justify-between shadow-lg">
        <span className="text-sm text-slate-500 font-medium">
          {tabs.length} Care Tab{tabs.length > 1 ? "s" : ""} Configured
        </span>
        <Button
          type="primary"
          size="large"
          icon={<Save size={18} />}
          loading={saveMutation.isPending}
          onClick={() => form.submit()}
          className="!bg-primary flex items-center gap-2 px-8 shadow-md"
        >
          Save Configuration
        </Button>
      </div>
    </div>
  );
};

export default SculptureCareCMS;
